import {
  formatElapsed,
  runElapsedMs,
  stepElapsedMs,
  type BattleProcedureRunState,
} from "./model";
import type { BattleProcedureSequence, BattleProcedureStep } from "./steps";

export interface RunExportContext {
  /** Wall-clock stamps, already formatted by the caller from its own clock. */
  readonly finishedAt: string | null;
  readonly nowMs: number;
  readonly startedAt: string | null;
}

function standardLine(
  step: BattleProcedureStep,
  elapsedMs: number,
): string | null {
  if (step.countdownMinutes === undefined) return null;
  const standardMs = step.countdownMinutes * 60_000;
  const outcome = elapsedMs <= standardMs ? "met" : "not met";
  return `- ${String(step.countdownMinutes)} minute standard: **${outcome}** (${formatElapsed(elapsedMs)} against ${formatElapsed(standardMs)}).`;
}

function confirmationLine(
  step: BattleProcedureStep,
  confirmed: boolean,
): string | null {
  if (step.requiresConfirmation !== true) return null;
  return `- Mandatory confirmation: ${confirmed ? "confirmed by the trainee" : "**not confirmed**"}.`;
}

/**
 * Renders one completed or partial run as Markdown: every step in the run, its
 * elapsed time, and everything the trainee typed, in order.
 *
 * R4.0 does not score. Nothing here marks, grades or assesses the trainee's
 * output; the twenty minute line records only whether the stated standard was
 * met, which is a measured time and not an assessment.
 */
export function exportRunMarkdown(
  state: BattleProcedureRunState,
  sequence: BattleProcedureSequence,
  runSteps: readonly BattleProcedureStep[],
  context: RunExportContext,
): string {
  const lines: string[] = [
    "# Battle Procedure Run",
    "",
    "| Field | Value |",
    "| --- | --- |",
    `| Started | ${context.startedAt ?? "not started"} |`,
    `| Finished | ${context.finishedAt ?? "not finished"} |`,
    `| Total elapsed | ${formatElapsed(runElapsedMs(state, context.nowMs))} |`,
    `| Steps in this run | ${String(runSteps.length)} of ${String(sequence.steps.length)} |`,
    `| Run scope | ${sequence.runScope.start} to ${sequence.runScope.end} |`,
    `| Step sequence source | ${sequence.source} |`,
    "",
    "This record is the trainee's own work. It is not scored and carries no",
    "assessment. It stays on this device.",
    "",
  ];

  for (const [index, step] of runSteps.entries()) {
    const record = state.steps[index];
    /* v8 ignore next -- the run state is built from these same steps */
    if (record === undefined) continue;
    const elapsedMs = stepElapsedMs(state, index, context.nowMs);
    const reached = record.startedAtMs !== null;
    lines.push(
      `## Step ${String(step.number)} — ${step.title}`,
      "",
      `- Phase: ${step.phase}`,
      `- Elapsed: ${reached ? formatElapsed(elapsedMs) : "not reached"}`,
    );
    if (reached) {
      const standard = standardLine(step, elapsedMs);
      if (standard !== null) lines.push(standard);
    }
    const confirmation = confirmationLine(step, record.confirmed);
    if (confirmation !== null) lines.push(confirmation);
    lines.push("");

    const text = record.text.trim();
    lines.push(text === "" ? "_No text recorded._" : text, "");
  }

  const omitted = sequence.steps.filter(({ inRun }) => !inRun);
  if (omitted.length > 0) {
    lines.push("## Steps outside this run", "", sequence.runScope.note, "");
    for (const step of omitted) {
      lines.push(`- Step ${String(step.number)} — ${step.title}`);
    }
    lines.push("");
  }

  return `${lines.join("\n").trimEnd()}\n`;
}

/** File name for a saved run. Contains no trainee content. */
export function runExportFileName(startedAt: string | null): string {
  const stamp = (startedAt ?? "unstarted").replaceAll(/[^0-9A-Za-z]/g, "-");
  return `battle-procedure-run-${stamp}.md`;
}
